import { API_BASE_URL } from '@/utils/constant.utils';
import { useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';

const SOCKET_URL = API_BASE_URL;

export type MessagePayload = {
  text: string;
};

export type ProcessingCompletePayload = {
  fileName: string;
  type: 'video' | 'image';
};

export type CheckFileResponse = {
  exists: boolean;
  fileName: string;
};

export type CheckImagesResponse = {
  exists: boolean;
  folderName: string;
  files?: string[];
};

export type CheckSuperpositionFileResponse = {
  exists: boolean;
  fileName: string;
};

export type InspectionProgressStatusUpdatePayload = {
  vehiculeCheckId: string;
  progressStatus: string;
};

export type InspectionVideoUploadProgress = {
  vehiculeCheckId: string;
  uploadId: string;
  progress: number; // percentage 0-100
  uploadedBytes: number;
  totalBytes: number;
  processing?: boolean; // Indicates if video is being processed
  message?: string; // Progress message with part information
  uploadedParts?: number; // Number of uploaded parts
  totalParts?: number; // Total number of parts
};

type UseSocketOptions = {
  onMessage?: (data: MessagePayload) => void;
  onInspectionProgressStatusUpdate?: (
    data: InspectionProgressStatusUpdatePayload,
  ) => void;
  onInspectionVideoUploadProgress?: (
    data: InspectionVideoUploadProgress,
  ) => void;
  onProcessingComplete?: (data: ProcessingCompletePayload) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
};

// Singleton socket manager - prevents multiple connections
class SocketManager {
  private socket: Socket | null = null;
  private listeners: Map<string, Set<(data: unknown) => void>> = new Map();
  private connectCallbacks: Set<() => void> = new Set();
  private disconnectCallbacks: Set<() => void> = new Set();
  private connectionPromise: Promise<Socket> | null = null;

  private getOrCreateSocket(): Promise<Socket> {
    if (this.socket?.connected) {
      return Promise.resolve(this.socket);
    }
    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    // Non-blocking connection
    this.connectionPromise = new Promise((resolve) => {
      setTimeout(() => {
        const socket = io(SOCKET_URL, {
          transports: ['websocket', 'polling'],
        });

        this.socket = socket;

        socket.on('connect', () => {
          console.warn('[Socket] Connected');
          this.connectionPromise = null;
          this.connectCallbacks.forEach((cb) => cb());
          resolve(socket);
        });

        socket.on('disconnect', () => {
          console.warn('[Socket] Disconnected');
          this.disconnectCallbacks.forEach((cb) => cb());
        });

        socket.on('connect_error', (error) => {
          console.error('[Socket] Connection error:', error);
          this.connectionPromise = null;
        });

        // Broadcast events to all subscribers
        socket.on('message', (data: MessagePayload) => {
          this.broadcast('message', data);
        });

        socket.on(
          'inspectionProgressStatusUpdate',
          (data: InspectionProgressStatusUpdatePayload) => {
            this.broadcast('inspectionProgressStatusUpdate', data);
          },
        );

        socket.on(
          'videoUploadProgress',
          (data: InspectionVideoUploadProgress) => {
            this.broadcast('videoUploadProgress', data);
          },
        );

        socket.on('processingComplete', (data: ProcessingCompletePayload) => {
          this.broadcast('processingComplete', data);
        });
      }, 0);
    });

    return this.connectionPromise;
  }

  private broadcast(event: string, data: unknown) {
    this.listeners.get(event)?.forEach((callback) => callback(data));
  }

  subscribe(event: string, callback: (data: unknown) => void): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);

    // Lazy connection - only connect when first subscriber
    if (!this.socket?.connected && !this.connectionPromise) {
      this.getOrCreateSocket().catch(() => {});
    }

    return () => {
      const callbacks = this.listeners.get(event);
      callbacks?.delete(callback);
      if (callbacks?.size === 0) {
        this.listeners.delete(event);
      }
    };
  }

  subscribeConnect(callback: () => void): () => void {
    this.connectCallbacks.add(callback);
    if (this.socket?.connected) {
      setTimeout(() => callback(), 0);
    } else if (!this.connectionPromise) {
      this.getOrCreateSocket().catch(() => {});
    }
    return () => this.connectCallbacks.delete(callback);
  }

  subscribeDisconnect(callback: () => void): () => void {
    this.disconnectCallbacks.add(callback);
    return () => this.disconnectCallbacks.delete(callback);
  }

  getSocket(): Socket | null {
    return this.socket;
  }

  async ensureConnected(): Promise<Socket> {
    return this.getOrCreateSocket();
  }
}

const socketManager = new SocketManager();

export const useSocket = ({
  onMessage,
  onProcessingComplete,
  onInspectionProgressStatusUpdate,
  onInspectionVideoUploadProgress,
  onConnect,
  onDisconnect,
}: UseSocketOptions) => {
  // Store callbacks in refs to avoid re-subscribing
  const callbacksRef = useRef({
    onMessage,
    onProcessingComplete,
    onInspectionProgressStatusUpdate,
    onInspectionVideoUploadProgress,
    onConnect,
    onDisconnect,
  });

  // Update refs when callbacks change
  useEffect(() => {
    callbacksRef.current = {
      onMessage,
      onProcessingComplete,
      onInspectionProgressStatusUpdate,
      onInspectionVideoUploadProgress,
      onConnect,
      onDisconnect,
    };
  }, [
    onMessage,
    onProcessingComplete,
    onInspectionProgressStatusUpdate,
    onInspectionVideoUploadProgress,
    onConnect,
    onDisconnect,
  ]);

  // Subscribe once on mount, using refs for callbacks
  useEffect(() => {
    const unsubscribes: (() => void)[] = [];
    const callbacks = callbacksRef.current;

    if (callbacks.onMessage) {
      unsubscribes.push(
        socketManager.subscribe('message', (data) => {
          callbacksRef.current.onMessage?.(data as MessagePayload);
        }),
      );
    }

    if (callbacks.onInspectionProgressStatusUpdate) {
      unsubscribes.push(
        socketManager.subscribe('inspectionProgressStatusUpdate', (data) => {
          callbacksRef.current.onInspectionProgressStatusUpdate?.(
            data as InspectionProgressStatusUpdatePayload,
          );
        }),
      );
    }

    if (callbacks.onInspectionVideoUploadProgress) {
      unsubscribes.push(
        socketManager.subscribe('videoUploadProgress', (data) => {
          callbacksRef.current.onInspectionVideoUploadProgress?.(
            data as InspectionVideoUploadProgress,
          );
        }),
      );
    }

    if (callbacks.onProcessingComplete) {
      unsubscribes.push(
        socketManager.subscribe('processingComplete', (data) => {
          callbacksRef.current.onProcessingComplete?.(
            data as ProcessingCompletePayload,
          );
        }),
      );
    }

    if (callbacks.onConnect) {
      unsubscribes.push(
        socketManager.subscribeConnect(() =>
          callbacksRef.current.onConnect?.(),
        ),
      );
    }

    if (callbacks.onDisconnect) {
      unsubscribes.push(
        socketManager.subscribeDisconnect(() =>
          callbacksRef.current.onDisconnect?.(),
        ),
      );
    }

    return () => unsubscribes.forEach((unsub) => unsub());
  }, []);

  const emitWithResponse = useCallback(
    <T>(event: string, payload: unknown): Promise<T> => {
      return new Promise((resolve) => {
        socketManager.ensureConnected().then((socket) => {
          socket.emit(event, payload, (response: T) => resolve(response));
        });
      });
    },
    [],
  );

  const sendMessage = useCallback((event: 'message', data: MessagePayload) => {
    socketManager.getSocket()?.emit(event, data);
  }, []);

  const checkFile = useCallback(
    (fileName: string) =>
      emitWithResponse<CheckFileResponse>('checkFile', { fileName }),
    [emitWithResponse],
  );

  const checkImages = useCallback(
    (folderName: string) =>
      emitWithResponse<CheckImagesResponse>('checkImages', { folderName }),
    [emitWithResponse],
  );

  const checkSuperpositionFile = useCallback(
    (id: string) =>
      emitWithResponse<CheckSuperpositionFileResponse>(
        'checkSuperpositionFile',
        { id },
      ),
    [emitWithResponse],
  );

  return {
    sendMessage,
    checkFile,
    checkImages,
    checkSuperpositionFile,
  };
};
